import { useEffect, useMemo, useRef, useState } from 'react'
import i18n from '@/i18n'
import { validateBrowserAsset } from '../utils/assets'
import {
  createCropPreview,
  cropAroundFocus,
  cropDialogUseful,
  cropForTarget,
  focusOfCrop,
  mediaWithFocus,
  emptyMediaReference,
  readImageDimensions,
  resolutionStatus,
  targetForProfile,
} from '../utils/imageCrops'

const RASTER_ACCEPT = 'image/png,image/jpeg,image/webp'
const BRAND_ACCEPT = `${RASTER_ACCEPT},image/svg+xml,image/x-icon,image/vnd.microsoft.icon`
const VECTOR_MIMES = ['image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon']

function warningCopy(status) {
  if (!status) return null
  const available = status.cropWidth
    ? `${status.cropWidth} × ${status.cropHeight}px`
    : `${status.sourceWidth} × ${status.sourceHeight}px`
  const recommended = `${status.requiredWidth} × ${status.requiredHeight}px`
  if (status.level === 'standard') {
    return i18n.t('editorV2.form.image.warningStandard', { available, recommended })
  }
  return i18n.t('editorV2.form.image.warningRetina', { available, recommended })
}

/**
 * The upload → validate → crop → rendition pipeline shared by every image control.
 *
 * Extracted from ImageEditorField so ImagePickerField can offer the same behaviour
 * behind a different layout without duplicating the crop and rendition logic — the
 * engine contract (addAsset → sourceAssetId → crop → renditionAssetId) lives here once.
 */
export function useImageMediaField({
  field,
  value,
  onChange,
  configuration,
  templateManifest,
  addAsset,
  addRenditionPreview,
  assetUrls,
  assetSourceUrls,
}) {
  const media = value || emptyMediaReference()
  const target = useMemo(
    () => targetForProfile(templateManifest, field.profile, configuration),
    [configuration, field.profile, templateManifest],
  )
  const [cropOpened, setCropOpened] = useState(false)
  const [localError, setLocalError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [sourceDimensions, setSourceDimensions] = useState(null)
  const generatedPreviewRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const sourceUrl = media.sourceAssetId
    ? (assetSourceUrls[media.sourceAssetId] || assetUrls[media.sourceAssetId]
      || configuration?.assets?.[media.sourceAssetId]?.url)
    : null
  const previewUrl = media.renditionAssetId ? assetUrls[media.renditionAssetId] : sourceUrl
  const cropMode = target?.mode === 'crop'
  // fit: the photo keeps its proportions (Story derives its split from them); only the focus is chosen.
  const fitMode = target?.mode === 'fit'
  const sourceMime = configuration?.assets?.[media.sourceAssetId]?.mime || ''
  const vectorSource = VECTOR_MIMES.includes(sourceMime)
    || /^(data:image\/(?:svg\+xml|x-icon|vnd\.microsoft\.icon))|(?:\.svg|\.ico)(?:$|\?)/i.test(sourceUrl || '')
  const sourceMetadata = configuration?.assets?.[media.sourceAssetId]
  const metadataDimensions = sourceMetadata?.width && sourceMetadata?.height
    ? { width: Number(sourceMetadata.width), height: Number(sourceMetadata.height) }
    : null
  const effectiveDimensions = sourceDimensions || metadataDimensions
  const cropUseful = cropMode && !vectorSource && cropDialogUseful(effectiveDimensions, target)
  const imageWarning = warningCopy(
    vectorSource ? null : resolutionStatus(media.crop, effectiveDimensions, target),
  )
  const accept = target?.mimeTypes?.some((type) => type.includes('svg') || type.includes('icon'))
    ? BRAND_ACCEPT
    : RASTER_ACCEPT

  useEffect(() => {
    if (cropOpened && (!media.sourceAssetId || (effectiveDimensions && !cropUseful))) {
      setCropOpened(false)
    }
  }, [cropOpened, cropUseful, effectiveDimensions, media.sourceAssetId])

  useEffect(() => {
    let cancelled = false
    setLocalError(null)
    setSourceDimensions(null)
    if (!media.sourceAssetId || vectorSource) {
      setSourceDimensions(null)
      return undefined
    }
    if (metadataDimensions) {
      setSourceDimensions(metadataDimensions)
      return undefined
    }
    if (!sourceUrl) return undefined
    readImageDimensions(sourceUrl)
      .then((dimensions) => { if (!cancelled) setSourceDimensions(dimensions) })
      .catch(() => {
        if (!cancelled) setLocalError(i18n.t('editorV2.form.image.errorDimensions'))
      })
    return () => { cancelled = true }
  }, [
    media.sourceAssetId,
    metadataDimensions?.height,
    metadataDimensions?.width,
    sourceUrl,
    vectorSource,
  ])

  useEffect(() => {
    if (
      !cropMode || cropOpened || !sourceUrl || !media.sourceAssetId
      || media.renditionAssetId || !effectiveDimensions || !target
    ) {
      return undefined
    }
    const signature = [
      media.sourceAssetId, JSON.stringify(media.crop), JSON.stringify(media.focalPoint), target.width, target.height,
    ].join(':')
    if (generatedPreviewRef.current === signature) return undefined
    generatedPreviewRef.current = signature
    let cancelled = false
    createCropPreview(sourceUrl, media.crop, target, media.focalPoint)
      .then(({ crop, previewUrl: nextPreviewUrl }) => {
        if (cancelled) return
        const renditionAssetId = addRenditionPreview(nextPreviewUrl)
        // Spread the reference rather than rebuild it: this effect bakes the
        // crop into a rendition, and the focus point behind that crop has to
        // survive it. Rebuilding from three keys dropped a generated focus the
        // moment its preview was made.
        onChangeRef.current({ ...media, renditionAssetId, crop })
      })
      .catch(() => {
        generatedPreviewRef.current = null
        if (!cancelled) setLocalError(i18n.t('editorV2.form.image.errorPreview'))
      })
    return () => { cancelled = true; generatedPreviewRef.current = null }
  }, [
    addRenditionPreview, cropMode, cropOpened, media, media.crop, media.renditionAssetId,
    media.sourceAssetId, sourceUrl, effectiveDimensions?.width, effectiveDimensions?.height,
    target?.height, target?.mode, target?.width,
  ])

  // The picture becomes the field's source: a crop slot starts around the tile's focus point (or
  // its saved crop); any other slot shows it whole.
  const selectSource = (
    sourceAssetId,
    dimensions,
    reference,
    { vector = false, rendition = null } = {},
  ) => {
    if (cropMode && !vector) {
      onChange({
        sourceAssetId,
        renditionAssetId: null,
        crop: reference.focalPoint
          ? cropAroundFocus(dimensions, target, reference.focalPoint)
          : cropForTarget(reference.crop, dimensions, target),
        ...(reference.focalPoint ? { focalPoint: reference.focalPoint } : {}),
      })
    } else {
      onChange({ sourceAssetId, renditionAssetId: rendition, crop: null })
    }
    setCropOpened(false)
  }

  // Resolves true once the file became the picture; false when it was refused (the field says why).
  // `origin`: the URL a library or stock picture was imported from (AD-234 library).
  const chooseFile = async (file, reference = {}, origin = null) => {
    setLocalError(null)
    setSourceDimensions(null)
    const assetError = validateBrowserAsset(file, target?.mimeTypes)
    if (assetError) {
      setLocalError(assetError)
      return false
    }
    const isVector = VECTOR_MIMES.includes(file.type)
    const rasterNeedsCrop = cropMode && !isVector
    const rasterNeedsContain = ['contain', 'fit'].includes(target?.mode) && !isVector
    let dimensions = null
    if (rasterNeedsCrop || rasterNeedsContain) {
      try {
        dimensions = await readImageDimensions(file)
        setSourceDimensions(dimensions)
      } catch {
        setLocalError(i18n.t('editorV2.form.image.errorRead'))
        return false
      }
    }
    const sourceAssetId = addAsset(file, {
      allowedTypes: target?.mimeTypes,
      sourceOnly: rasterNeedsCrop,
      // A pick from the ad's own library is already in it.
      library: reference?.source === 'ai'
        ? null
        : { sourceUrl: typeof origin === 'string' && /^https?:\/\//i.test(origin) ? origin : null },
    })
    if (!sourceAssetId) return false
    selectSource(sourceAssetId, dimensions, reference, {
      vector: isVector,
      rendition: sourceAssetId,
    })
    return true
  }

  // A library tile that is already one of this ad's stored pictures is chosen by its id: importing
  // it again stored a second copy, which the library could not match to the tile.
  const chooseStored = (assetId, reference = {}) => {
    const record = configuration?.assets?.[assetId]
    if (record?.kind !== 'source' || !record.width || !record.height) return false
    const allowed = !record.mime || !target?.mimeTypes?.length
      || target.mimeTypes.includes(record.mime)
    if (!allowed) return false
    const dimensions = { width: Number(record.width), height: Number(record.height) }
    setLocalError(null)
    setSourceDimensions(dimensions)
    selectSource(assetId, dimensions, reference, {
      vector: VECTOR_MIMES.includes(record.mime || ''),
    })
    return true
  }

  /**
   * Adopts a remote image (advertiser website or Unsplash) as if it had been uploaded.
   *
   * The bytes are pulled through our own origin: advertiser hosts do not send CORS
   * headers, so a direct fetch could display the image but never read it into a File.
   * Resolves true once imported; false when it was not (the field shows why).
   */
  const adoptUrl = async (url, filename, reference) => {
    if (!url) return false
    const storedId = reference?.assetId ? String(reference.assetId) : ''
    if (storedId && !storedId.startsWith('pending-') && chooseStored(storedId, reference)) return true
    setLocalError(null)
    setBusy(true)
    try {
      // A picture of the ad's own library (an upload not saved yet, or our stored copy) is read
      // directly; anything else comes through our proxy.
      const own = url.startsWith('data:') || new URL(url, window.location.href).origin === window.location.origin
        || Object.values(assetSourceUrls || {}).includes(url)
      const endpoint = own ? url : `/api/studio-editor-v2/media-library/fetch?url=${encodeURIComponent(url)}`
      const response = await fetch(endpoint, { credentials: 'same-origin' })
      if (!response.ok) {
        let message = i18n.t('editorV2.form.image.errorImport')
        try {
          const payload = await response.json()
          if (payload?.message) message = payload.message
        } catch { /* keep the default */ }
        setLocalError(message)
        return false
      }
      const blob = await response.blob()
      const name = filename || (url.split('/').pop() || 'image').split('?')[0] || 'image'
      return await chooseFile(new File([blob], name, { type: blob.type }), reference || {}, own ? null : url)
    } catch {
      setLocalError(i18n.t('editorV2.form.image.errorImport'))
      return false
    } finally {
      setBusy(false)
    }
  }

  // What the picker shows. A stored point is the one that was chosen — by the
  // pipeline or by hand; without one, the centre of the current crop is where
  // this image is already looking, which beats pretending nothing was decided.
  const focalPoint = media.focalPoint || focusOfCrop(media.crop)
  // Only worth offering where a crop is actually taken: a logo is placed whole,
  // so there is nothing for a focus point to decide.
  const focusUseful = (cropMode || fitMode) && !vectorSource && Boolean(media.sourceAssetId)
    && Boolean(effectiveDimensions) && Boolean(target)

  const setFocalPoint = (point) => {
    if (fitMode) {
      onChange({ ...media, focalPoint: { x: Math.round(point.x * 100) / 100, y: Math.round(point.y * 100) / 100 } })
      return
    }
    onChange(mediaWithFocus(media, effectiveDimensions, target, point))
  }

  const applyCrop = ({ crop, previewUrl: nextPreviewUrl }) => {
    const renditionAssetId = addRenditionPreview(nextPreviewUrl)
    // A box drawn by hand supersedes the point it was derived from, so the
    // stored focus is dropped; the picker then falls back to the box's centre.
    onChange({ sourceAssetId: media.sourceAssetId, renditionAssetId, crop })
    setCropOpened(false)
  }

  const clear = () => {
    setLocalError(null)
    setSourceDimensions(null)
    onChange(emptyMediaReference())
  }

  return {
    media,
    target,
    accept,
    sourceUrl,
    previewUrl,
    cropMode,
    cropUseful,
    imageWarning,
    localError,
    busy,
    cropOpened,
    setCropOpened,
    focalPoint,
    focusUseful,
    setFocalPoint,
    chooseFile,
    adoptUrl,
    applyCrop,
    clear,
  }
}
